/**
 * Centralized API Client with Cookie-based Authentication
 * Uses HTTP-only session cookies for 3PM Auth SSO
 */

import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config';

// Create centralized axios instance
const api: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: config.REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with cross-origin requests
});

// Request interceptor - No Authorization header needed (cookies handle auth)
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Cookies are automatically sent with withCredentials: true
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're already redirecting to prevent loops
let isRedirecting = false;
let lastRedirectPath = "";

// Response interceptor - Handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    // Handle 401 errors - redirect to login (but only for protected routes)
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      
      // Don't redirect if:
      // 1. Already redirecting
      // 2. On auth pages (callback, login)
      // 3. On home page without user (already showing login overlay)
      // 4. Request was to /auth/me (expected to fail when logged out)
      const isAuthPage = currentPath.includes("/auth/callback") || 
                         currentPath.includes("/auth/login");
      const isHomePage = currentPath === "/";
      const isAuthMeRequest = error.config?.url?.includes("/auth/me");
      const hasNoUser = !localStorage.getItem("user");
      
      // Clear stale user data
      if (hasNoUser) {
        localStorage.removeItem("user");
      }
      
      // Only redirect if:
      // - Not already redirecting
      // - Not on auth pages
      // - Not a /auth/me request (expected to fail when logged out)
      // - Not on home page without user (already showing login)
      if (!isRedirecting && !isAuthPage && !isAuthMeRequest && !(isHomePage && hasNoUser)) {
        // Prevent redirecting to the same path
        if (lastRedirectPath !== currentPath) {
          isRedirecting = true;
          lastRedirectPath = currentPath;
          
          // Redirect to login (which will redirect to IdP)
          const returnUrl = encodeURIComponent(currentPath);
          window.location.href = `/auth/login?returnUrl=${returnUrl}`;
          
          // Reset flag after delay
          setTimeout(() => { 
            isRedirecting = false;
            lastRedirectPath = "";
          }, 2000);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
